import { Link } from "react-router-dom";
import { useState } from "react";
import { Linkedin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EnprexMark } from "./Logo";

const LINKEDIN_URL = "https://www.linkedin.com/company/enprex-services";

const FOOTER_LINKS = {
  Services: [
    { label: "ETRM & Openlink Endur Consulting", to: "/services/endur-consulting" },
    { label: "Trading Technology & Transformation", to: "/services/digital-transformation" },
  ],
  Industries: [
    { label: "Energy & Commodities", to: "/industries" },
  ],
  Company: [
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
  ],
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#050A14] border-t border-white/5 mt-24">
      <div className="container-x py-20">
        {/* Newsletter — compact */}
        <div className="grid lg:grid-cols-2 gap-10 pb-14 border-b border-white/5">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-3 font-mono">
              — The EnPrex Brief
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-light text-white tracking-tight leading-snug max-w-md">
              Practical insights on energy, technology, transformation & AI.
            </h3>
          </div>
          <form onSubmit={subscribe} className="flex flex-col justify-center" data-testid="newsletter-form">
            <label className="text-[10px] font-medium text-slate-400 mb-2 uppercase tracking-[0.2em] font-mono">
              Your work email
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                className="flex-1 bg-white/5 border border-white/10 text-white px-5 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/40 outline-none transition-all"
              />
              <button
                data-testid="newsletter-submit-button"
                type="submit"
                disabled={loading}
                className="bg-[#D4AF37] text-[#0B1F3A] px-6 py-3 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-[#FDE047] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? "Subscribing..." : "Subscribe"} <ArrowRight size={14} />
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 font-mono">
              Occasional insights. No noise.
            </p>
          </form>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-14">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-4 mb-6">
              <EnprexMark size={40} />
              <div className="leading-none">
                <div className="font-display text-white text-xl font-medium tracking-[-0.02em]">En<span className="font-semibold">Prex</span></div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="block w-3 h-px bg-[#D4AF37]" />
                  <span className="text-[10px] text-[#D4AF37] uppercase tracking-[0.45em] font-medium">Services</span>
                </div>
              </div>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              Specialist consulting for energy and commodity trading technology.
            </p>
            <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500">
              Energy · Precision · Excellence
            </div>
            <div className="flex items-center gap-3 mt-6">
              <a
                data-testid="social-linkedin"
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="EnPrex Services on LinkedIn"
                className="w-9 h-9 flex items-center justify-center border border-white/10 text-slate-400 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
              >
                <Linkedin size={15} />
              </a>
              <a
                href="mailto:info@enprexservices.com"
                className="text-sm text-slate-400 hover:text-[#D4AF37] transition-colors"
              >
                info@enprexservices.com
              </a>
            </div>
          </div>

          {Object.entries(FOOTER_LINKS).map(([heading, links]) => (
            <div key={heading}>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white mb-5 font-mono">
                {heading}
              </div>
              <ul className="space-y-3">
                {links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-slate-400 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">
            © {new Date().getFullYear()} EnPrex Services. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-xs text-slate-500 font-mono uppercase tracking-wider">
            <a href="/sitemap.xml" className="hover:text-white transition-colors">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
